import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { BellRing, AlertOctagon, AlertTriangle, Info, Check, Eye, ExternalLink, RotateCcw } from "lucide-react";

const PRIO = {
  Critical: { cls: "border-rose-200 bg-rose-50 text-rose-700", icon: AlertOctagon, dot: "bg-rose-500" },
  Warning: { cls: "border-amber-200 bg-amber-50 text-amber-700", icon: AlertTriangle, dot: "bg-amber-500" },
  Info: { cls: "border-sky-200 bg-sky-50 text-sky-700", icon: Info, dot: "bg-sky-500" },
};
const STATUS_LABEL = { Baru: "Baru", Dibaca: "Dibaca", Selesai: "Selesai" };
const MODULES = ["Semua", "Produksi", "Purchasing", "Inventory"];
const PRIOS = ["Semua", "Critical", "Warning", "Info"];

export default function Alerts() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [fMod, setFMod] = useState("Semua");
  const [fPrio, setFPrio] = useState("Semua");
  const [showResolved, setShowResolved] = useState(false);

  const load = () => { setRows(null); api.get("/alerts").then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(load, []);

  const act = async (path, key, msg) => {
    try { await api.post(`/alerts/${path}`, { key }); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = (rows || []).filter((a) =>
    (fMod === "Semua" || a.module === fMod) &&
    (fPrio === "Semua" || a.priority === fPrio) &&
    (showResolved || a.status !== "Selesai"));

  return (
    <div>
      <PageHeader title="Pusat Notifikasi" subtitle="Peringatan operasional lintas modul — stok, produksi, dan purchasing." testId="alerts-header" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
          {MODULES.map((m) => (
            <button key={m} onClick={() => setFMod(m)} data-testid={`alert-mod-${m}`}
              className={cn("rounded-md px-3 py-1.5 text-sm font-medium transition-colors", fMod === m ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800")}>{m}</button>
          ))}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
          {PRIOS.map((p) => (
            <button key={p} onClick={() => setFPrio(p)} data-testid={`alert-prio-${p}`}
              className={cn("rounded-md px-3 py-1.5 text-sm font-medium transition-colors", fPrio === p ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800")}>{p === "Semua" ? "Semua" : p}</button>
          ))}
        </div>
        <Button variant={showResolved ? "default" : "outline"} size="sm" onClick={() => setShowResolved(!showResolved)} data-testid="alert-toggle-resolved" className={showResolved ? "bg-slate-700 hover:bg-slate-600" : ""}>
          {showResolved ? "Sembunyikan Selesai" : "Tampilkan Selesai"}
        </Button>
      </div>

      {!rows ? <Loading label="Memuat notifikasi..." /> : filtered.length === 0 ? (
        <EmptyState title="Tidak ada notifikasi" subtitle="Semua kondisi operasional aman." icon={BellRing} />
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => {
            const p = PRIO[a.priority] || PRIO.Info;
            const Icon = p.icon;
            return (
              <div key={a.key} data-testid={`alert-${a.key}`}
                className={cn("flex items-start gap-3 rounded-xl border bg-white p-4 shadow-sm", a.status === "Selesai" && "opacity-60")}>
                <div className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border", p.cls)}><Icon className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-sm font-semibold text-slate-800">{a.jenis}</span>
                    <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase", p.cls)}>{a.priority}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{a.module}</span>
                    {a.status !== "Baru" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{STATUS_LABEL[a.status]}</span>}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{a.description}</p>
                  <p className="mt-1 text-[11px] text-slate-400">{formatDateTime(a.created_at)} · Ref: <span className="font-mono">{a.reference_id?.slice(0, 8)}</span></p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-indigo-600" onClick={() => navigate(a.link)} data-testid={`alert-open-${a.key}`}><ExternalLink className="mr-1 h-3.5 w-3.5" /> Buka</Button>
                  {a.status === "Baru" && <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={() => act("read", a.key, "Ditandai dibaca")} data-testid={`alert-read-${a.key}`}><Eye className="mr-1 h-3.5 w-3.5" /> Dibaca</Button>}
                  {a.status !== "Selesai" ? <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={() => act("resolve", a.key, "Ditandai selesai")} data-testid={`alert-resolve-${a.key}`}><Check className="mr-1 h-3.5 w-3.5" /> Selesai</Button>
                    : <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={() => act("unresolve", a.key, "Dibuka kembali")} data-testid={`alert-reopen-${a.key}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Buka Lagi</Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
